class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    window.InvisaWearSubscriptionLogin?.updateLinks();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState?.id;

    const finishRender = () => {
      window.InvisaWearSubscriptionLogin?.afterCartDrawerDomUpdate('renderContents');

      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.open();
        if (window.InvisaWearRecharge?.scheduleApplyCartDrawerSubscriptionUI) {
          window.InvisaWearRecharge.scheduleApplyCartDrawerSubscriptionUI(0, 'renderContents:afterOpen');
        }
      });
    };

    const sections = parsedState?.sections;
    const sectionsToRender = this.getSectionsToRender();
    const hasAllSectionHtml =
      sections &&
      sectionsToRender.every((section) => {
        const html = sections[section.id];
        return typeof html === 'string' && html.length > 0;
      });

    if (!hasAllSectionHtml) {
      this.fetchSectionsHtml(sections)
        .then(finishRender)
        .catch((error) => {
          console.error(error);
          finishRender();
        });
      return;
    }

    sectionsToRender.forEach((section) => {
      this.replaceSectionContents(section, sections[section.id]);
    });

    finishRender();
  }

  replaceSectionContents(section, sectionHtml) {
    const sectionElement = section.selector
      ? document.querySelector(section.selector)
      : document.getElementById(section.id);

    if (!sectionElement || !sectionHtml) return;

    const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    if (innerHTML === null) return;

    sectionElement.innerHTML = innerHTML;
  }

  fetchSectionsHtml(existingSections = {}) {
    const sectionsToRender = this.getSectionsToRender();

    return Promise.all(
      sectionsToRender.map((section) => {
        const cachedHtml = existingSections?.[section.id];
        if (typeof cachedHtml === 'string' && cachedHtml.length > 0) {
          return Promise.resolve({ section, sectionHtml: cachedHtml });
        }

        return fetch(`${routes.cart_url}?section_id=${section.id}`)
          .then((response) => response.text())
          .then((sectionHtml) => ({ section, sectionHtml }));
      })
    ).then((results) => {
      results.forEach(({ section, sectionHtml }) => {
        this.replaceSectionContents(section, sectionHtml);
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const target = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return target ? target.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function openCartDrawerFromUrlParam() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('cart_drawer') !== 'open') return;

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    if (window.InvisaWearRecharge?.scheduleApplyCartDrawerSubscriptionUI) {
      window.InvisaWearRecharge.scheduleApplyCartDrawerSubscriptionUI(0, 'url:cart_drawer=open');
    }
  }

  params.delete('cart_drawer');
  const newSearch = params.toString();
  const cleanUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', cleanUrl);

  window.InvisaWearSubscriptionLogin?.updateLinks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openCartDrawerFromUrlParam);
} else {
  openCartDrawerFromUrlParam();
}
